import React, { useContext, useState, useEffect } from "react";
import { Link, withRouter } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Breadcrumb,
  Button,
  Header,
  Loader,
  Popup,
  Icon,
  Table,
  Label,
  Message,
} from "semantic-ui-react";
import ReportRangeForm from "../components/ReportRangeForm";
import { GET_STUDENT, ON_COMMENT_ADDED_FOR_STUDENT } from "../GraphQL";
import UserContext from "contexts/UserContext";
import moment from "moment";
import findIndex from "lodash/findIndex";
import { capitalize } from "../utils";

const StudentPage = ({ match, history }) => {
  const user = useContext(UserContext);
  const [moreLoading, setMoreLoading] = useState(false);
  const { error, loading, data, fetchMore, subscribeToMore } = useQuery(
    GET_STUDENT,
    {
      variables: { id: match.params.id },
    }
  );

  const onReportRangeSubmit = ({
    startDate,
    endDate,
    showComments,
    showUnsentReports,
  }) => {
    // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_media_queries/Printing
    return new Promise((resolve) => {
      const hideFrame = document.createElement("iframe");

      const onMessage = (evt) => {
        console.log("message received", evt);
        if (evt.data !== "reportsReady") {
          console.log("ignoring message");
          return;
        }
        resolve();

        window.removeEventListener("message", onMessage);
        console.log("received message", evt);
        const closePrint = () => {
          document.body.removeChild(hideFrame);
        };
        hideFrame.contentWindow.onbeforeunload = closePrint;
        hideFrame.contentWindow.onafterprint = closePrint;
        hideFrame.contentWindow.print();
      };
      window.addEventListener("message", onMessage);
      hideFrame.style.display = "none"; // hide iframe
      hideFrame.src = `/students/${match.params.id}/reports?startDate=${startDate}&endDate=${endDate}&showComments=${showComments}&showUnsentReports=${showUnsentReports}`;
      document.body.appendChild(hideFrame);
    });
  };

  useEffect(() => {
    if (data) {
      subscribeToMore({
        document: ON_COMMENT_ADDED_FOR_STUDENT,
        variables: { student: match.params.id },
        updateQuery: (prev, { subscriptionData }) => {
          console.log("new comment", subscriptionData);
          if (!subscriptionData.data) return prev;
          const newComment = subscriptionData.data.commentAddedForStudent;
          console.log("previous", prev);

          const reportIndex = findIndex(prev.getStudent.pagedReports.reports, {
            id: newComment.report,
          });

          const retVal = JSON.parse(JSON.stringify(prev));
          if (reportIndex !== -1) {
            retVal.getStudent.pagedReports.reports[
              reportIndex
            ].unreadCount = true;
          }
          return retVal;
        },
      });
    }
    // eslint-disable-next-line
  }, [data]);

  if (loading) {
    return <Loader active />;
  } else if (error) {
    throw error;
  }

  const student = data.getStudent;

  const renderStatusCell = (report) =>
    report.sent ? (
      <Table.Cell positive>
        <Icon name="checkmark" /> {report.read ? "Read" : "Sent"}
      </Table.Cell>
    ) : (
      <Table.Cell warning>
        <Icon name="attention" /> Unsent
      </Table.Cell>
    );

  return (
    <div>
      <Breadcrumb size="tiny">
        <Breadcrumb.Section>
          <Link to="/">Home</Link>
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>{student.name} </Breadcrumb.Section>
      </Breadcrumb>
      {user.canEditStudents &&
      student.parents.length === 0 &&
      !student.archived ? (
        <Message info>
          <Message.Content>
            <Message.Header>No Parents/Guardians found.</Message.Header>
            This student has no parents/guardians. Please{" "}
            <Link to={`/students/${student.id}/edit`}>
              update this student's settings
            </Link>{" "}
            to include at least one parent/guardian.
          </Message.Content>
        </Message>
      ) : (
        false
      )}
      {user.canEditStudents ? (
        <Link to={`/students/${student.id}/edit`}>
          <Button basic floated="right">
            <Button.Content>
              <Icon name="edit outline" />
              <span className="mobile-hidden"> Edit Student</span>
            </Button.Content>
          </Button>
        </Link>
      ) : null}
      {user.canEditStudents ? (
        <Link to={`/students/${student.id}/edit/questions`}>
          <Button basic floated="right">
            <Button.Content>
              <Icon name="question circle" />
              <span className="mobile-hidden"> Edit Questions</span>
            </Button.Content>
          </Button>
        </Link>
      ) : null}
      <Header as="h2" dividing>
        {student.name}{" "}
        {student.archived ? <Label color="grey">Archived</Label> : false}
      </Header>
      {user.canEditReports && !student.archived ? (
        <Link to={`/students/${student.id}/reports/new`}>
          <Button basic floated="right">
            <Button.Content>
              <Icon name="plus" /> New Report
            </Button.Content>
          </Button>
        </Link>
      ) : null}
      {user.isPro() ? (
        <Popup
          trigger={
            <Button basic floated="right">
              <Button.Content>
                <Icon name="print" /> Print Reports
              </Button.Content>
            </Button>
          }
          on="click"
          position="bottom center"
          content={<ReportRangeForm onSubmit={onReportRangeSubmit} />}
        />
      ) : null}
      <Header as="h3">Reports</Header>
      <Table celled unstackable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Date</Table.HeaderCell>
            <Table.HeaderCell>Report Type</Table.HeaderCell>
            {user.canEditReports ? (
              <Table.HeaderCell>Status</Table.HeaderCell>
            ) : null}
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {student.pagedReports.reports.length === 0 ? (
            <Table.Row>
              <Table.Cell>No reports found.</Table.Cell>
            </Table.Row>
          ) : (
            false
          )}
          {student.pagedReports.reports.map((report) => (
            <Table.Row key={report.id}>
              <Table.Cell style={{ position: "relative" }}>
                {report.unreadCount ? (
                  <Label color="teal" size="mini" horizontal>
                    <Icon name="comment" /> New comment
                  </Label>
                ) : null}
                {moment(report.date).format("dddd, LL")}
                {report.files && report.files.length > 0 ? (
                  <Icon name="attach" />
                ) : null}
              </Table.Cell>
              <Table.Cell>{capitalize(report.reportType)}</Table.Cell>
              {user.canEditReports ? renderStatusCell(report) : null}
              <Table.Cell>
                {user.canEditReports && report.sent === false ? (
                  <Link
                    to={`/students/${student.id}/reports/${report.id}/edit`}
                  >
                    <Button basic color="blue" type="button" size="tiny">
                      <Icon name="edit outline" /> Edit
                    </Button>
                  </Link>
                ) : null}
                <Link to={`/students/${student.id}/reports/${report.id}`}>
                  <Button basic color="teal" type="button" size="tiny">
                    <Icon name="eye" /> View
                  </Button>
                </Link>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {student.pagedReports.nextToken ? (
        <Button
          fluid
          loading={moreLoading}
          onClick={async () => {
            setMoreLoading(true);
            await fetchMore({
              query: GET_STUDENT,
              variables: {
                id: student.id,
                nextToken: student.pagedReports.nextToken,
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                console.log(previousResult);
                console.log(fetchMoreResult);
                const newReportConnection =
                  fetchMoreResult.getStudent.pagedReports;
                return {
                  getStudent: Object.assign({}, previousResult.getStudent, {
                    pagedReports: Object.assign(
                      {},
                      previousResult.getStudent.pagedReports,
                      {
                        reports: [
                          ...previousResult.getStudent.pagedReports.reports,
                          ...newReportConnection.reports,
                        ],
                        nextToken: newReportConnection.nextToken,
                      }
                    ),
                  }),
                };
              },
            });
            setMoreLoading(false);
          }}
        >
          Show More Reports
        </Button>
      ) : null}
    </div>
  );
};

export default withRouter(StudentPage);
